import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { format, money, PAYMENT_METHOD_LABELS, PAYMENT_METHODS, type CurrencyCode, type PaymentMethod } from '@iobiz/shared'
import type { PaiementSaisi } from '../../data/repositories/sales.write.js'
import { ConfirmDialog } from '../../ui/ConfirmDialog.js'

/**
 * Fenêtre d'encaissement (W-15).
 *
 * ⭐ Le rendu de monnaie est affiché en grand et en permanence. C'est le seul
 * chiffre que le caissier doit lire sous la pression d'une file d'attente ;
 * l'obliger à le calculer de tête est la première source d'erreur de caisse.
 */

export function EncaissementDialog({
  open,
  du,
  devise,
  horsLigne,
  onAnnuler,
  onValider,
}: {
  open: boolean
  /** Montant à régler, en plus petite unité. */
  du: number
  devise: CurrencyCode
  horsLigne: boolean
  onAnnuler: () => void
  onValider: (paiements: PaiementSaisi[]) => void
}): JSX.Element {
  const [methode, setMethode] = useState<PaymentMethod>('cash')
  const [saisi, setSaisi] = useState('')
  const [deja, setDeja] = useState<PaiementSaisi[]>([])
  /**
   * ⚠️ Un encaissement est un fait comptable : le journal des règlements est en
   * ajout seul, et le document reçoit un numéro légal. On ne le défait pas d'un
   * clic. La confirmation existe pour marquer ce passage, pas pour faire joli.
   */
  const [confirmation, setConfirmation] = useState(false)
  /**
   * La monnaie a-t-elle ÉTÉ RENDUE ?
   *
   * ⭐ Au comptoir, le tiroir est parfois vide de petites coupures : on
   * encaisse le billet entier et l'on reste devoir la différence au client.
   * Cela arrivait déjà — cela ne se notait nulle part, et la dette se réglait
   * de mémoire, ou pas.
   *
   * ⚠️ Vrai par défaut : c'est le cas courant, et faire cocher « oui j'ai bien
   * rendu la monnaie » à chaque vente de la journée ferait cocher sans lire.
   */
  const [rendue, setRendue] = useState(true)

  const prix = (v: number): string => format(money(v, devise), 'fr-FR')

  useEffect(() => {
    if (open) {
      setDeja([])
      setSaisi('')
      setMethode('cash')
      setConfirmation(false)
      setRendue(true)
    }
  }, [open])

  const regle = deja.reduce((s, p) => s + p.amount, 0)
  const restant = Math.max(0, du - regle)
  const recu = Number(saisi.replace(/[^\d]/g, '')) || 0

  // Le rendu ne concerne QUE les espèces : une carte ne rend pas la monnaie.
  const rendu = methode === 'cash' ? Math.max(0, recu - restant) : 0

  /** Coupures usuelles au-dessus du montant dû — le geste le plus fréquent. */
  const suggestions = useMemo(() => {
    const base = [restant]
    for (const pas of [1000, 5000, 10_000]) {
      const arrondi = Math.ceil(restant / pas) * pas
      if (arrondi > restant && !base.includes(arrondi)) base.push(arrondi)
    }
    return base.slice(0, 4)
  }, [restant])

  function ajouter(): void {
    const montant = Math.min(recu || restant, restant)
    if (montant <= 0) return
    setDeja((p) => [...p, { method: methode, amount: montant }])
    setSaisi('')
  }

  /**
   * Rassemble TOUT ce que le caissier a saisi, y compris ce qui est encore
   * dans le champ.
   *
   * ⚠️ « Valider » ignorait la frappe en cours dès qu'un règlement avait été
   * ajouté : 10 000 ajoutés puis 4 000 tapés partaient en 10 000, et les
   * 4 000 disparaissaient sans un mot. Même défaut que les étiquettes client,
   * même correction — ce qui est À L'ÉCRAN fait partie de ce qu'on croit
   * enregistrer.
   */
  function reglements(): PaiementSaisi[] {
    const liste = [...deja]
    // Champ vide ET rien d'ajouté : c'est le cas courant, on solde la vente.
    if (recu === 0 && liste.length === 0) {
      return [{ method: methode, amount: restant }]
    }
    if (recu > 0) {
      const montant = Math.min(recu, restant)
      const remis = methode === 'cash' && recu > montant ? recu : undefined
      /**
       * ⭐ Monnaie NON rendue : on impute au document la somme réellement
       * encaissée, pas seulement ce qui était dû. Le document devient alors
       * trop-perçu, et son solde signé (`soldeDocument`) dit que la boutique
       * doit au client — ce qui le fait remonter dans les colonnes
       * « Reste dû » de la liste des ventes et de la fiche client.
       */
      const garde = remis !== undefined && !rendue
      liste.push({
        method: methode,
        amount: garde ? remis : montant,
        ...(remis === undefined
          ? {}
          : { tendered: remis, change: garde ? 0 : remis - montant }),
      })
    }
    return liste
  }

  function valider(): void {
    onValider(reglements())
  }

  /** Ce qui sera réellement encaissé, et ce qui restera dû après validation. */
  const encaisse = reglements().reduce((somme, p) => somme + p.amount, 0)
  const resteApres = Math.max(0, du - encaisse)

  /**
   * ⚠️ Le PAIEMENT PARTIEL est désormais validable, et c'était un défaut
   * grave : la condition exigeait `recu >= restant`, si bien qu'un client qui
   * réglait 14 000 sur 15 000 laissait « Valider » grisé. Le bouton
   * « + Paiement partiel » ajoutait pourtant bien la somme — puis le caissier
   * restait bloqué. La seule issue était de vider le champ et de valider, ce
   * qui enregistrait la vente comme INTÉGRALEMENT RÉGLÉE : les 14 000
   * n'existaient plus nulle part, et la créance de 1 000 non plus.
   *
   * Le produit sait pourtant tenir une créance — statut « partiellement
   * réglée », reste dû, relance. C'est l'écran qui l'interdisait.
   *
   * Il reste à refuser le vide : encaisser zéro n'est pas un encaissement.
   */
  const suffisant = encaisse > 0

  return (
    <Dialog open={open} onClose={onAnnuler} fullWidth maxWidth="xs">
      <DialogTitle>Encaissement</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', letterSpacing: '.06em' }}>
            À PAYER
          </Typography>
          <Typography variant="h2" className="tabular" sx={{ fontWeight: 800 }}>
            {prix(restant)}
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={methode}
          onChange={(_, v: PaymentMethod | null) => v && setMethode(v)}
          sx={{ mb: 4, flexWrap: 'wrap' }}
        >
          {PAYMENT_METHODS.map((m) => (
            <ToggleButton key={m} value={m} sx={{ flex: '1 0 30%' }}>
              {PAYMENT_METHOD_LABELS[m]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          label="Montant reçu"
          value={saisi}
          onChange={(e) => setSaisi(e.target.value)}
          fullWidth
          autoFocus
          inputMode="numeric"
          placeholder={String(restant)}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          {suggestions.map((s) => (
            <Chip key={s} label={prix(s)} onClick={() => setSaisi(String(s))} variant="outlined" />
          ))}
        </Box>

        {/* ⚠️ La question n'apparaît QUE s'il y a de la monnaie à rendre :
            posée systématiquement, elle deviendrait un réflexe et ne dirait
            plus rien le jour où elle compte. */}
        {methode === 'cash' && rendu > 0 && (
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox checked={rendue} onChange={(e) => setRendue(e.target.checked)} />
              }
              label="La monnaie a été rendue au client"
            />
            {!rendue && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                La boutique restera devoir {prix(rendu)} au client. Le montant apparaîtra en
                négatif dans « Reste dû », sur la vente et sur sa fiche.
              </Alert>
            )}
          </Box>
        )}

        {methode === 'cash' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography sx={{ color: 'text.secondary' }}>Rendu monnaie</Typography>
            <Typography
              className="tabular"
              sx={{ fontWeight: 700, fontSize: '1.35rem', color: rendu > 0 ? 'success.main' : 'text.primary' }}
            >
              {prix(rendu)}
            </Typography>
          </Box>
        )}

        {deja.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            {deja.map((p, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {PAYMENT_METHOD_LABELS[p.method]}
                </Typography>
                <Typography variant="body2" className="tabular">
                  {prix(p.amount)}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Retirer ce règlement"
                  onClick={() => setDeja((liste) => liste.filter((_, index) => index !== i))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </>
        )}

        <Button size="small" onClick={ajouter} disabled={restant <= 0 || recu <= 0} sx={{ mt: 2 }}>
          + Paiement partiel
        </Button>

        {/* ⭐ Ce qui RESTERA DÛ, avant de valider. Sans cette ligne, un
            règlement partiel se validait comme un autre et le caissier
            n'apprenait la créance qu'en rouvrant la fiche — s'il la
            rouvrait. */}
        {resteApres > 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            Règlement partiel : {prix(encaisse)} sur {prix(du)}. Il restera dû{' '}
            <strong>{prix(resteApres)}</strong>.
          </Alert>
        )}

        {horsLigne && (
          <Alert severity="info" sx={{ mt: 4 }}>
            Hors ligne : la vente est enregistrée sur cet appareil et recevra son numéro
            définitif au retour du réseau. Le ticket porte une référence provisoire.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onAnnuler}>Annuler</Button>
        <Button
          variant="contained"
          size="large"
          disabled={!suffisant}
          onClick={() => setConfirmation(true)}
        >
          Valider
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmation}
        title="Valider cet encaissement ?"
        message={
          // ⚠️ La CRÉANCE est annoncée en premier quand il y en a une : c'est
          // l'information qui change la suite de la journée, et elle passait
          // auparavant sous silence — la vente partait pour soldée.
          (resteApres > 0
            ? `Règlement partiel : ${prix(encaisse)} encaissés sur ${prix(du)}. ` +
              `Il restera dû ${prix(resteApres)}, et la vente sera marquée « partiellement réglée ». `
            : '') +
          'La vente sera enregistrée et le règlement inscrit définitivement : ' +
          'un encaissement ne se modifie pas, il se corrige. ' +
          'Un administrateur pourra annuler la vente, ce qui rendra le stock.'
        }
        confirmLabel="Valider l’encaissement"
        onClose={() => setConfirmation(false)}
        onConfirm={async () => {
          valider()
        }}
      />
    </Dialog>
  )
}
